"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type EricKind,
  type EricLabels,
  ericIsReady,
  ERIC_FIELDS as F,
  formatEric,
} from "@/lib/tool-kit/citation-sources-more/eric-document-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const ERIC_DOCUMENT_CITATION_GENERATOR_STRINGS = {
  formHeading: "ERIC record details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the title of the document or article to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What is the ERIC number?",
  kindDocument: "ED — a report, paper or other document",
  kindArticle: "EJ — a journal article",
  authorsLabel: "Author(s)",
  authorsPlaceholder: "Cori Brewster",
  authorsHint:
    "One per line, as First Last or Last, First, as the record lists them. ERIC prints them as Last, First.",
  organizationLabel: "The author is an organization, not a person",
  titleLabel: "Title",
  titlePlaceholder:
    "Full-day kindergarten: Exploring an option for extended learning",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. ERIC capitalizes every word, so fix it. MLA and Chicago get title case automatically.",
  dateLabel: "Date published",
  datePlaceholder: "YYYY-MM-DD",
  dateHint:
    "ERIC shows the year and month. A year alone is fine; MLA and Chicago add the month when you give it.",
  ericLabel: "ERIC number",
  ericPlaceholder: "ED472733",
  ericHint:
    "ED and six digits for a document, EJ and six digits for an article, from the ERIC record.",
  publisherLabel: "Publisher (optional, MLA and Chicago)",
  publisherPlaceholder: "Northwest Regional Educational Laboratory",
  publisherHint:
    "APA names ERIC as the source and leaves the publisher out. MLA and Chicago name it when the record gives one.",
  urlLabel: "URL (optional)",
  urlPlaceholder: "https://files.eric.ed.gov/fulltext/ED472733.pdf",
  urlHint:
    "The full-text PDF address when ERIC hosts the file. With none, a document gets its ERIC record page.",
  journalLabel: "Journal",
  journalPlaceholder: "Educational Leadership",
  volumeLabel: "Volume",
  issueLabel: "Issue",
  pagesLabel: "Pages",
  pagesPlaceholder: "34-39",
  doiLabel: "DOI (optional)",
  doiPlaceholder: "10.1000/example",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 treats an ERIC document as a work of limited circulation: the ED number goes in parentheses after the italic title, ERIC is the source, and the link is the full-text PDF.",
  apaArticleNote:
    "An EJ record is a journal article, so APA 7 cites the article itself. ERIC is how you found it, not part of the reference.",
  mlaNote:
    "The MLA Handbook has no ERIC example. This treats the document as a stand-alone work held in a database: ERIC is the container in italics, followed by the ED number and the link.",
  chicagoNote:
    "Chicago 18 has no ERIC example. A document is cited as a report with ERIC and its number where a database goes; an article is a journal article, with the ERIC number only when there is no DOI or link.",
  exampleDocument: {
    authors: "Cori Brewster\nJennifer Railsback",
    title: "Full-day kindergarten: Exploring an option for extended learning",
    date: "2002-12",
    eric: "ED472733",
    publisher: "Northwest Regional Educational Laboratory",
    url: "https://files.eric.ed.gov/fulltext/ED472733.pdf",
  },
  exampleArticle: {
    authors: "Carol S. Dweck",
    title: "The perils and promises of praise",
    date: "2007-10",
    eric: "EJ777079",
    journal: "Educational Leadership",
    volume: "65",
    issue: "2",
    pages: "34-39",
  },
} as const;

type Strings = typeof ERIC_DOCUMENT_CITATION_GENERATOR_STRINGS;

function leadFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    { id: F.organization, kind: "checkbox", label: strings.organizationLabel },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.dateHint,
    },
    {
      id: F.eric,
      kind: "text",
      label: strings.ericLabel,
      placeholder: strings.ericPlaceholder,
      hint: strings.ericHint,
    },
  ];
}

function documentFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
      hint: strings.publisherHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
    },
  ];
}

function articleFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.journal,
      kind: "text",
      label: strings.journalLabel,
      placeholder: strings.journalPlaceholder,
    },
    { id: F.volume, kind: "text", label: strings.volumeLabel },
    { id: F.issue, kind: "text", label: strings.issueLabel },
    {
      id: F.pages,
      kind: "text",
      label: strings.pagesLabel,
      placeholder: strings.pagesPlaceholder,
    },
    {
      id: F.doi,
      kind: "url",
      label: strings.doiLabel,
      placeholder: strings.doiPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function ericLabels(strings: Strings): EricLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    apaArticleNote: strings.apaArticleNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function EricDocumentCitationGeneratorWidget() {
  const strings = useToolUi(ERIC_DOCUMENT_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<EricKind>("document");
  const fields = useMemo(
    () => [
      ...leadFields(strings),
      ...(kind === "document"
        ? documentFields(strings)
        : articleFields(strings)),
    ],
    [strings, kind]
  );
  const labels = useMemo(() => ericLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatEric(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "document" ? strings.exampleDocument : strings.exampleArticle
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="eric-kind"
          onChange={setKind}
          options={[
            { value: "document", label: strings.kindDocument },
            { value: "article", label: strings.kindArticle },
          ]}
          value={kind}
        />
      }
      isReady={ericIsReady}
      strings={strings}
    />
  );
}
